import { useEffect, useState } from "react";
import { Input } from "antd";
import { t } from "i18next";
import { Trans } from "react-i18next";
import classNames from "classnames";
import { Button } from "@kalodata/kds-design";
import { IconLoading } from "@icons/index";
import { IconFolder } from "@icons/index";
import { IconLineBox, IconLineClose } from "@kalodata/kds-design-icons";
import { CreateProductEntityModal } from "@pages/library/CreateProductEntityModal";
import { SelectProductModal } from "./SelectProductModal";
import { StudioSectionLabel } from "./StudioSectionLabel";
import { ProductBoundDetails } from "./ProductBoundDetails";
import { useVideoRemakeStudio } from "./VideoRemakeStudioContext";
import { isNotNil } from "@kalodata/tools";

/**
 * Product to Promote 区域 —— 整合三种入口：
 * - 链接 input：直接粘贴 TikTok / Kalodata / Amazon 链接，回车 / 失焦触发解析
 * - 方块按钮（icon-line-box）：唤起 CreateProductEntityModal 手动创建
 * - Add From Library：唤起 SelectProductModal（Created / Saved 双 tab）
 *
 * 已绑定商品后下方内联展开 <ProductBoundDetails />。
 */
export const ProductToPromoteSection = () => {
  const {
    productOrigin,
    parsingLink,
    linkError,
    setLinkError,
    parseProductLink,
    pickProductFromLibrary,
    acceptManualCreated,
    clearProduct,
    form,
  } = useVideoRemakeStudio();

  const [linkInput, setLinkInput] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);

  // 解析成功 / 选中商品后回填到 input；清空商品时清空 input
  useEffect(() => {
    if (productOrigin === "link" && form.sourceUrl) {
      setLinkInput(form.sourceUrl.replace(/^https?:\/\//, ""));
    } else if (productOrigin === null) {
      setLinkInput("");
    }
  }, [productOrigin, form.sourceUrl]);

  const handleParseLink = async () => {
    const value = linkInput.trim();
    if (!value || parsingLink) return;
    setLinkError(null);
    await parseProductLink(value);
  };

  // 解析中（parsingLink）先隐藏下方明细：解析成功才展示，解析失败由 productOrigin 重置为 null 同样保持隐藏
  const showProductBound = isNotNil(productOrigin) && !parsingLink;

  return (
    <section className="flex w-full flex-col gap-[8px]">
      <div className="flex h-[30px] items-center justify-between py-[4px]">
        <StudioSectionLabel required tooltip={t("Select or paste a product to feature in the remade video.")}>
          <Trans>Product to Promote</Trans>
        </StudioSectionLabel>
        <button
          type="button"
          onClick={() => setLibraryOpen(true)}
          className="flex cursor-pointer items-center gap-[4px] border-0 bg-transparent p-0 text-[14px] leading-[24px] text-kds-fg-soft transition-colors hover:text-kds-fg"
        >
          <IconFolder className="h-[16px] w-[16px]" />
          <Trans>Add from library</Trans>
        </button>
      </div>

      <div className="flex w-full items-start gap-[8px]">
        <div
          className={classNames(
            "flex h-[56px] flex-1 items-center rounded-[8px] border bg-kds-bg-element px-[12px] shadow-[0px_1px_1.5px_0px_var(--kds-shadow)] transition-colors",
            linkError ? "border-kds-danger-border" : "border-kds-border-solid",
          )}
        >
          <div className="flex items-center gap-[8px] pl-[4px] pr-[12px]">
            <span className="text-[16px] font-semibold leading-[24px] text-kds-fg">https://</span>
            <div className="h-[48px] w-px shrink-0 bg-kds-border-solid" />
          </div>
          <Input
            value={linkInput}
            onChange={(e) => {
              setLinkInput(e.target.value);
              if (linkError) setLinkError(null);
            }}
            onPressEnter={handleParseLink}
            onBlur={handleParseLink}
            variant="borderless"
            placeholder={t("Paste TikTok, Amazon (US, UK, JP, DE), or Kalodata product link")}
            className="h-[24px] flex-1 px-0! text-[14px] text-kds-fg placeholder:text-kds-fg-subtle"
            disabled={parsingLink}
          />
          {parsingLink && <IconLoading className="ml-[8px] h-[16px] w-[16px] animate-spin text-kds-fg-soft" />}
          {!parsingLink && productOrigin === "link" && (
            <button
              type="button"
              aria-label={t("Clear product")}
              className="ml-[8px] flex size-[20px] cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-kds-fg-soft hover:text-kds-fg"
              onClick={(e) => {
                e.stopPropagation();
                clearProduct();
              }}
            >
              <IconLineClose size={14} />
            </button>
          )}
        </div>

        <Button
          variant="outline"
          icon={<IconLineBox size={24} className="text-kds-fg" />}
          aria-label={t("Create product manually")}
          className="h-[56px] w-[56px] shrink-0 rounded-[8px] border-kds-border-solid shadow-[0px_1px_1.5px_0px_var(--kds-shadow)]"
          onClick={() => setCreateOpen(true)}
        />
      </div>

      {linkError && (
        <div className="text-[12px] leading-[16px] text-kds-danger-fg">
          {"* "}
          {linkError}
        </div>
      )}

      {showProductBound && <ProductBoundDetails />}

      <CreateProductEntityModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        defaultMode="manual"
        onSuccess={(detail) => {
          if (detail) acceptManualCreated(detail);
        }}
      />

      <SelectProductModal
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={(payload) => pickProductFromLibrary(payload)}
      />
    </section>
  );
};
